import { db } from '../../config/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

// ─── Types ───

export interface TreatmentGoal {
  id: string;
  description: string;
  status: 'active' | 'achieved' | 'modified' | 'discontinued';
  targetDate?: string;
  objectives: TreatmentObjective[];
}

export interface TreatmentObjective {
  id: string;
  description: string;
  measurable: string; // SMART measurable criteria
  status: 'not-started' | 'in-progress' | 'achieved';
  interventions: TreatmentIntervention[];
}

export interface TreatmentIntervention {
  id: string;
  description: string;
  frequency?: string; // e.g., "Weekly", "Each session"
  modality?: string;  // e.g., "CBT", "DBT", "Motivational Interviewing"
}

export interface TreatmentPlan {
  id: string;
  patientId: string;
  providerId: string;
  providerName: string;

  // Headway 6 requirements
  diagnosis: string;           // Primary diagnosis (ICD-10) — legacy / display string
  diagnoses: { code: string; name: string }[];  // Structured diagnoses (primary + secondary)
  presentingProblem: string;   // Why the client is seeking treatment
  goals: TreatmentGoal[];     // Treatment goals with objectives + interventions
  reviewDate: string;          // Next review date

  // Metadata
  sessionFrequency?: string;  // e.g., "Weekly", "Bi-weekly"
  estimatedDuration?: string; // e.g., "12 weeks", "6 months"
  clientSignature?: boolean;
  clientSignedAt?: Date;

  status: 'draft' | 'active' | 'completed' | 'revised';
  createdAt: Date;
  updatedAt: Date;
}

// ─── Helpers ───

function stripUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

function generateId(): string {
  return doc(collection(db, '_')).id;
}

// ─── Service ───

export class FirebaseTreatmentPlanService {
  private static COLLECTION = 'treatment_plans';

  static async createTreatmentPlan(
    data: Omit<TreatmentPlan, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, this.COLLECTION));
    await setDoc(docRef, {
      ...stripUndefined(data as any),
      goals: data.goals || [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      data.providerId, 'provider', 'create', 'form' as any, docRef.id, data.patientId,
      'Created treatment plan'
    );
    return docRef.id;
  }

  static async updateTreatmentPlan(
    planId: string,
    providerId: string,
    patientId: string,
    updates: Partial<TreatmentPlan>
  ): Promise<void> {
    const { id, createdAt, ...rest } = updates as any;
    await updateDoc(doc(db, this.COLLECTION, planId), {
      ...stripUndefined(rest),
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      providerId, 'provider', 'update', 'form' as any, planId, patientId,
      'Updated treatment plan'
    );
  }

  static subscribeToPatientPlans(
    patientId: string,
    callback: (plans: TreatmentPlan[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const plans = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          clientSignedAt: data.clientSignedAt?.toDate?.() || undefined,
        } as TreatmentPlan;
      });
      callback(plans);
    });
  }

  static subscribeToProviderPatientPlans(
    providerId: string,
    patientId: string,
    callback: (plans: TreatmentPlan[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('providerId', '==', providerId),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const plans = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          clientSignedAt: data.clientSignedAt?.toDate?.() || undefined,
        } as TreatmentPlan;
      });
      callback(plans);
    });
  }

  // Helper to create a blank goal template
  static createBlankGoal(): TreatmentGoal {
    return {
      id: generateId(),
      description: '',
      status: 'active',
      objectives: [this.createBlankObjective()],
    };
  }

  static createBlankObjective(): TreatmentObjective {
    return {
      id: generateId(),
      description: '',
      measurable: '',
      status: 'not-started',
      interventions: [this.createBlankIntervention()],
    };
  }

  static createBlankIntervention(): TreatmentIntervention {
    return {
      id: generateId(),
      description: '',
      frequency: 'Each session',
    };
  }
}
